import { cn } from "@/lib/utils";
import type { ProblemDifficulty } from "../types";

/**
 * `EX-PLR01`: the one difficulty idiom of every Judge list (`docs/03` §6), the
 * "Signal" tag the founder chose on 2026-10-22. A 6 px tag with a hairline
 * ring and a three-step glyph, one bar for Easy, two for Medium, three for
 * Hard, so the level reads without its colour. Easy is the deeper emerald;
 * Medium and Hard keep the amber and red they had. `onDark` is the same tag
 * on the Judge's near-black.
 */
const STEPS: Readonly<Record<ProblemDifficulty, number>> = { easy: 1, medium: 2, hard: 3 };

const TAG_CLASS: Readonly<Record<ProblemDifficulty, string>> = {
  easy: "bg-emerald-50 text-emerald-800 ring-emerald-800/15",
  medium: "bg-amber-50 text-amber-700 ring-amber-700/20",
  hard: "bg-red-50 text-red-700 ring-red-700/20",
};

const TAG_CLASS_DARK: Readonly<Record<ProblemDifficulty, string>> = {
  easy: "bg-emerald-500/15 text-emerald-300 ring-emerald-400/30",
  medium: "bg-amber-500/15 text-amber-300 ring-amber-400/30",
  hard: "bg-red-500/15 text-red-300 ring-red-400/30",
};

const GLYPH_CLASS: Readonly<Record<ProblemDifficulty, string>> = {
  easy: "text-emerald-700",
  medium: "text-amber-600",
  hard: "text-red-600",
};

const GLYPH_CLASS_DARK: Readonly<Record<ProblemDifficulty, string>> = {
  easy: "text-emerald-400",
  medium: "text-amber-400",
  hard: "text-red-400",
};

/** The glyph's colours as a fill: a light-surface meter's bar and key (`LevelMeter`). */
export const DIFFICULTY_FILL: Readonly<Record<ProblemDifficulty, string>> = {
  easy: "bg-emerald-700",
  medium: "bg-amber-600",
  hard: "bg-red-600",
};

/** The three bars alone: the tag's glyph, and the difficulty chips' key. */
export function DifficultyGlyph({
  difficulty,
  onDark = false,
  className,
}: {
  difficulty: ProblemDifficulty;
  onDark?: boolean;
  className?: string;
}) {
  const steps = STEPS[difficulty];
  return (
    <svg
      viewBox="0 0 12 12"
      aria-hidden
      className={cn("h-3 w-3 shrink-0", (onDark ? GLYPH_CLASS_DARK : GLYPH_CLASS)[difficulty], className)}
    >
      <rect x="0" y="7" width="3" height="5" rx="1" fill="currentColor" opacity={1} />
      <rect x="4.5" y="4" width="3" height="8" rx="1" fill="currentColor" opacity={steps >= 2 ? 1 : 0.24} />
      <rect x="9" y="1" width="3" height="11" rx="1" fill="currentColor" opacity={steps >= 3 ? 1 : 0.24} />
    </svg>
  );
}

export function DifficultyTag({
  difficulty,
  label,
  onDark = false,
  className,
}: {
  difficulty: ProblemDifficulty;
  /** The reader's word for the level; the caller translates it. */
  label: string;
  onDark?: boolean;
  /** Placement only (`shrink-0`, a margin); the look is this file's. */
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex h-6 shrink-0 items-center gap-[7px] whitespace-nowrap rounded-md pl-2 pr-2.5 text-[12.5px] font-bold tracking-[0.01em] ring-1 ring-inset",
        (onDark ? TAG_CLASS_DARK : TAG_CLASS)[difficulty],
        className,
      )}
    >
      <DifficultyGlyph difficulty={difficulty} onDark={onDark} />
      {label}
    </span>
  );
}
